import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitComparisonView } from "@shared/models/analytics/unit"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import { get, partial } from "lodash-es"
import type { PreparedAction } from "../../../action-doc-builder"
import type { Narrative } from "../../create-narrative"
import { followContext } from "../COURSE_FOLLOW_COURSE"

interface Payload {
  unitId: string
  course: Course
  planbook: Planbook
  /** Viewed from this class, which follows `course`; `course`'s teacher isn't told about it. */
  followingCourseId?: string
}

export default function unitComparisonViewAction(payload: Payload): PreparedAction {
  return {
    name: "UNIT_COMPARISON_VIEW",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = {
    course: payload.course,
    planbook: findFn("planbook", get(payload.course, "relationships.planbook.data.id") as string),
    unit: findFn("card-stack", payload.unitId) || findFn("card-stack-summary", payload.unitId),
  }

  let newEvent: UnitComparisonView = {
    ...createBlankEvent(),
    root_id: payload.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: null,
    primary_verb: "view",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),

    primary_object_type: "unit",
    primary_object_id: payload.unitId,
    primary_object_specifier: null,
    primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
  }

  return {
    title: "Viewed the Unit Comparison Modal",
    titleForAnalytics: "Unit Comparison - View",
    description: `Viewed the ${models.unit.attributes.title} unit, from ${payload.course.attributes.title}`,
    context: {
      course: payload.course.attributes.title,
      planbookTitle: get(models, "planbook.attributes.title"),
      unitId: payload.unitId,
      courseId: get(models, "course.id"),
      planbookId: get(models, "planbook.id"),
      ...(payload.followingCourseId === undefined
        ? {}
        : followContext({
            followingCourseId: payload.followingCourseId,
            followedCourseId: payload.course.id,
          })),
    },
    activity: payload.followingCourseId
      ? null
      : {
          actor: `user-${userId}`,
          verb: "viewed",
          object: `course-${payload.course.id}`,
          to: [
            `course-edits:course-${payload.course.id}`,
            `planbook-edits:planbook-${models.planbook.id}`,
          ],
        },
    newAnalyticsEvents: [newEvent],
  }
}
